import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  DriveEta,
  Email,
  LocalGasStation,
  Person,
  Phone,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Player } from "@lottiefiles/react-lottie-player";
import { Howl } from "howler";
import emailjs from "emailjs-com";
import successAnimation from "./successAnimation.json"; // your animation file

const carBrands = [
  "Maruthi Suziki",
  "Hyundai",
  "Honda",
  "Tata",
  "Ford",
  "Volkswagon",
  "Mahindra",
  "Renault",
  "Chevrolet",
  "Toyata",
  "Skoda",
  "Nissan",
  "Fiat",
  "Datsun",
  "BMW",
  "Kia",
  "Audi",
  "Mercedes",
  "Jeep",
  "Mitsubishi",
  "MG",
  "Land Rover",
  "Volvo",
  "Jaguar",
  "Isuzu",
  "Mini",
  "Porsche",
  "Aston Martin",
  "Citreon",
  "Lexus",
  "Bently",
  "Ferrari",
  "Lamborgini",
  "Rolls Royace",
  "BYD",
  "Hummer",
  "Premier",
];

const fuelTypes = ["Petrol", "Diesel", "Electric", "Hybrid"];

const ContactForm = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    car: "",
    model: "",
    fuel: "",
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const templateParams = {
      full_name: formData.name,
      user_phoneNumber: formData.mobile,
      user_email: formData.email,
      user_car: formData.car,
      user_carModel: formData.model,
      user_carFuel: formData.fuel,
    };

    emailjs
      .send(
        "service_4wjcbsg",
        "template_jckv6eu",
        templateParams,
        "F3vMWAyM8impvHd21"
      )
      .then((response) => {
        console.log("SUCCESS!", response.status, response.text);

        // ✅ Ocean wave sound
        const oceanSound = new Howl({
          src: [
            "https://assets.mixkit.co/sfx/preview/mixkit-achievement-bell-600.mp3",
          ],
        });
        oceanSound.once("load", () => oceanSound.play()); // ensures load first

        setLoading(false);
        setShowSuccess(true);

        setTimeout(() => {
          setShowSuccess(false);
          onClose();
          setFormData({
            name: "",
            mobile: "",
            email: "",
            car: "",
            model: "",
            fuel: "",
          });
        }, 6000);
      })
      .catch((error) => {
        console.error("Failed to send email:", error);
        alert("Failed to send email. Please try again.");
        setLoading(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(to right, orangered, orange)",
          color: "white",
          fontWeight: "bold",
          textAlign: "center",
          fontSize: "1.5rem",
          position: "relative",
          py: 2,
        }}
      >
        {showSuccess ? "Thank You!" : "Book Your Car Service"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 16, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 3 }}>
        {showSuccess ? (
          <Box textAlign="center" py={4}>
            <Player
              autoplay
              keepLastFrame
              src={successAnimation}
              style={{ height: 200, width: 200 }}
            />
            <Typography
              variant="h6"
              sx={{ mt: 2, fontWeight: "bold", color: "orangered" }}
            >
              Booking Confirmed!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Our team will get in touch with you shortly.
            </Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: "orangered" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Mobile Number"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: "orangered" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "orangered" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Car Brand"
              name="car"
              value={formData.car}
              onChange={handleChange}
              margin="normal"
              select
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DriveEta sx={{ color: "orangered" }} />
                  </InputAdornment>
                ),
              }}
            >
              {carBrands.map((brand) => (
                <MenuItem key={brand} value={brand}>
                  {brand}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Car Model"
              name="model"
              value={formData.model}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DriveEta sx={{ color: "orangered" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Fuel Type"
              name="fuel"
              value={formData.fuel}
              onChange={handleChange}
              margin="normal"
              select
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalGasStation sx={{ color: "orangered" }} />
                  </InputAdornment>
                ),
              }}
            >
              {fuelTypes.map((fuel) => (
                <MenuItem key={fuel} value={fuel}>
                  {fuel}
                </MenuItem>
              ))}
            </TextField>

            <Box mt={4} textAlign="center">
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: "25px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  background: "linear-gradient(to right, orangered, orange)",
                  color: "white",
                  boxShadow: "0px 6px 15px rgba(255, 69, 0, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(to right, #ff5722, #ff9800)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Submit"
                )}
              </Button>
            </Box>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactForm;
